import React, { useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import atlassianChampionsLogo from '../../assets/images/champions/atlassian-champions-logo.png';
import Button from '@atlaskit/button';
import { submitChampionSignup } from '@atlassian/community-library';
import { FormErrors, FormValues, initialValues } from '../../types/ChampionApplicationFormTypes';
import { validateChampionApplication } from '../../utils/ChampionApplicationFormValidation';

import { PersonalInfoSection } from './sections/PersonalInfoSection';
import { AddressSection } from 'components/champions/sections/AddressSection';
import { WorkPartnerSection } from 'components/champions/sections/WorkPartnerSection';
import { ProductsSection } from 'components/champions/sections/ProductsSection';
import { MotivationSection } from 'components/champions/sections/MotivationSection';
import { PreferenceSection } from 'components/champions/sections/PreferenceSection';
import { PreferenceDetailsSection } from 'components/champions/sections/PreferenceDetailsSection';
import { HowHeardSection } from 'components/champions/sections/HowHeardSection';
import { AgreementsSection } from 'components/champions/sections/AgreementsSection';

type ChampionApplicationFormProps = {
  accountIsManaged: boolean;
  emailId: string;
  profileId: string;
  userName: string;
  homeBaseUrl: string;
};

const ChampionApplicationForm: React.FC<ChampionApplicationFormProps> = ({
  accountIsManaged,
  emailId,
  profileId,
  userName,
  homeBaseUrl,
}) => {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const updateField = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateMultiSelect = (key: keyof FormValues, optionId: string) => {
    setValues((prev) => {
      const current = prev[key] as string[];
      const exists = current.includes(optionId);
      const next = exists ? current.filter((id) => id !== optionId) : [...current, optionId];
      return { ...prev, [key]: next as any };
    });
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateChampionApplication(values, { accountIsManaged });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    const baseUrl =
      process.env['REACT_APP_ENVI'] === 'local' ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];
    try {
      const result = await submitChampionSignup(values, baseUrl, baseUrl);
      if (result?.success) {
        setSubmitStatus({ type: 'success', message: 'Your application has been submitted successfully!' });
        setValues(initialValues);
        setErrors({});
      } else {
        setSubmitStatus({
          type: 'error',
          message: result?.message ?? 'Submission failed. Please try again.',
        });
      }
    } catch {
      setSubmitStatus({ type: 'error', message: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="champion-form" css={outerStyles}>
      <div css={formColumnStyles}>
        <div css={logoContainerStyles}>
          <div css={logoCenterStyles}>
            <img
              src={atlassianChampionsLogo}
              alt="Atlassian Champions"
              css={logoStyles}
            />
          </div>
        </div>
        <div css={innerStyles}>
          <form css={formStyles} onSubmit={handleSubmit}>
            <h1 css={headingLargeStyles}>Apply to be an Atlassian Champion</h1>

          <p css={[introTextStyles, marginBottomXSmallStyles]}>
            Questions marked with an asterisk<abbr css={introAsteriskStyles} title="required">*</abbr> are required.
          </p>
          <p css={[introTextStyles, marginBottomMediumStyles]}>
            Issues with this form? We&rsquo;re here to help. Email us at communitymanagers@atlassian.com
          </p>

          <h2 css={headingMediumStyles}>Before you begin</h2>
          <p css={[introTextStyles, marginBottomLargeStyles]}>
            We&rsquo;re excited for you to start your champion journey. You&rsquo;ll need to meet some requirements to
            join the program. See{' '}
            <a
              href="https://community.atlassian.com/champions#whos-eligible-to-apply"
              target="_blank"
              rel="noopener noreferrer"
            >
              Who&rsquo;s eligible to apply?
            </a>
          </p>

          <PersonalInfoSection
            values={values}
            updateField={updateField}
            emailId={emailId}
            profileId={profileId}
            userName={userName}
            homeBaseUrl={homeBaseUrl}
            errors={{
              firstName: errors.firstName,
              lastName: errors.lastName,
              email: errors.email,
              communityProfileUrl: errors.communityProfileUrl,
              linkedinUrl: errors.linkedinUrl,
            }}
          />

          <AddressSection
            values={values}
            updateField={updateField}
            errors={{
              street: errors.street,
              city: errors.city,
              stateProvince: errors.stateProvince,
              postalCode: errors.postalCode,
              country: errors.country,
            }}
          />

          <WorkPartnerSection values={values} updateField={updateField} errors={{ partnerType: errors.partnerType }} />

          <ProductsSection
            values={values}
            updateMultiSelect={updateMultiSelect}
            updateField={updateField}
            errors={{ products: errors.products, otherAtlassianProducts: errors.otherAtlassianProducts }}
          />

          <MotivationSection
            values={values}
            updateField={updateField}
            errors={{ whyBecomeChampion: errors.whyBecomeChampion }}
          />

          <PreferenceSection
            values={values}
            updateField={updateField}
            accountIsManaged={accountIsManaged}
            errors={{ championPreference: errors.championPreference }}
          />

          <PreferenceDetailsSection
            values={values}
            updateField={updateField}
            errors={{
              howContributeOnline: errors.howContributeOnline,
              attendedAce: errors.attendedAce,
              aceExperience: errors.aceExperience,
              fourEventsPerYear: errors.fourEventsPerYear,
              activityAndEvent: errors.activityAndEvent,
              whyToCreateContent: errors.whyToCreateContent,
              contentSharingFrequency: errors.contentSharingFrequency,
              bestContent1: errors.bestContent1,
              bestContent2: errors.bestContent2,
              bestContent3: errors.bestContent3,
              primarySocialProfile: errors.primarySocialProfile,
              commitSharingContent: errors.commitSharingContent,
              trainingFrequency: errors.trainingFrequency,
              trainingSources: errors.trainingSources,
              activeCug: errors.activeCug,
              eventsPerYear: errors.eventsPerYear,
              discussInCoE: errors.discussInCoE,
              coeFocus: errors.coeFocus,
              otherExcellence: errors.otherExcellence,
            }}
          />

          <HowHeardSection
            values={values}
            updateField={updateField}
            errors={{
              howHeard: errors.howHeard,
              howHeardOther: errors.howHeardOther,
              howHeardEmployee: errors.howHeardEmployee,
              referrerName: errors.referrerName,
            }}
          />

          <AgreementsSection
            values={values}
            updateField={updateField}
            errors={{ agreePrivacy: errors.agreePrivacy, agreeNda: errors.agreeNda }}
          />

          {submitStatus?.type === 'success' && (
            <div css={successBannerStyles}>{submitStatus.message}</div>
          )}
          {submitStatus?.type === 'error' && (
            <div css={errorBannerStyles}>{submitStatus.message}</div>
          )}

          <div css={marginTopLargeStyles} className="champion-form-submit">
            <Button
              type="submit"
              appearance="primary"
              isDisabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting\u2026' : 'Submit'}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default ChampionApplicationForm;

const outerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 0,
  overflow: 'visible',
  padding: token('space.600'),
  paddingTop: token('space.075'), /* minimal distance from nav bar */
  /* SLDS-matching compact text inputs: 2rem height, 0.8125rem font */
  '& [data-ds--text-field--container]': {
    height: '2rem',
    minHeight: '2rem',
    maxHeight: '2rem',
    padding: 0,
    borderRadius: '0.25rem',
    borderColor: '#c9c9c9',
    backgroundColor: '#fff',
  },
  '& [data-ds--text-field--container] input': {
    fontSize: '0.8125rem',
    padding: '0 0.75rem',
    height: '100%',
  },
});

const formColumnStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const logoContainerStyles = css({
  width: '100%',
  maxWidth: '686px', /* 10% larger than 624px */
  marginBottom: '12px', /* slightly more space between logo and heading */
});

const innerStyles = css({
  width: '100%',
  maxWidth: '600px',
});

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.0'),
});

const logoCenterStyles = css({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
});

const logoStyles = css({
  maxWidth: '686px', /* 10% larger than 624px */
  width: '100%',
  height: 'auto',
});

const headingLargeStyles = css({
  fontSize: '22.4pt', /* 30% smaller than 32pt */
  fontWeight: 600,
  fontFamily: 'sans-serif',
  marginBottom: token('space.300'),
});

const headingMediumStyles = css({
  fontSize: '18.2pt', /* 30% smaller than 26pt */
  fontWeight: 600,
  fontFamily: 'sans-serif',
  marginTop: token('space.100'),
  marginBottom: token('space.200'),
});

const introTextStyles = css({
  fontSize: '10pt',
  fontFamily: 'sans-serif',
});

const marginBottomXSmallStyles = css({
  marginBottom: token('space.100'),
});

const marginBottomMediumStyles = css({
  marginBottom: token('space.200'),
});

const marginBottomLargeStyles = css({
  marginBottom: token('space.400'),
});

const marginTopLargeStyles = css({
  marginTop: token('space.200'),
});

/* Intro asterisk: same color as text, not red */
const introAsteriskStyles = css({
  color: 'inherit',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const successBannerStyles = css({
  padding: token('space.200'),
  borderRadius: '3px',
  backgroundColor: token('color.background.success'),
  color: token('color.text.success'),
  fontWeight: 600,
});

const errorBannerStyles = css({
  padding: token('space.200'),
  borderRadius: '0.25rem',
  backgroundColor: '#fef1ee',
  color: 'var(--champion-form-error)',
  fontWeight: 400,
});
